import React, { createContext, useState, useEffect, useContext } from 'react';
import mockUsers from '../data/mockUsers';

// Create context
export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  // State for the current user
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);

  // Initialize with mock data
  useEffect(() => {
    // In a real app, this would be an API call
    setUsers(mockUsers);
    
    // Check for existing session
    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) {
      setCurrentUser(JSON.parse(savedUser));
      setIsAuthenticated(true);
    }
    
    setLoading(false);
  }, []);

  // Login function
  const login = (username, password) => {
    const user = mockUsers.find(
      (user) => user.username === username && user.password === password
    );

    if (user) {
      setCurrentUser(user);
      setIsAuthenticated(true);
      localStorage.setItem('currentUser', JSON.stringify(user));
      return { success: true };
    }
    
    return { 
      success: false, 
      message: 'Invalid username or password'
    };
  };

  // Logout function
  const logout = () => {
    setCurrentUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('currentUser');
  };

  // Update user profile
  const updateProfile = (updatedProfile) => {
    const updatedUser = { ...currentUser, ...updatedProfile };
    
    // Update in users array
    const updatedUsers = users.map(user => 
      user.id === updatedUser.id ? updatedUser : user
    );
    
    setCurrentUser(updatedUser);
    setUsers(updatedUsers);
    localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    
    return { success: true };
  };

  // Toggle anonymous mode
  const toggleAnonymousMode = () => {
    const updatedUser = { 
      ...currentUser, 
      anonymousMode: !currentUser.anonymousMode 
    };
    
    return updateProfile(updatedUser);
  };

  // Update visibility settings
  const updateVisibilitySetting = (field, value) => {
    const updatedVisibilitySettings = {
      ...currentUser.visibilitySettings,
      [field]: value
    };
    
    return updateProfile({ visibilitySettings: updatedVisibilitySettings });
  };

  // Get user by ID
  const getUserById = (userId) => {
    return users.find(user => user.id === userId) || null;
  };

  // Get filtered users based on criteria
  const getFilteredUsers = (filters = {}) => {
    let filteredUsers = [...users];
    
    // Filter by destination if provided
    if (filters.destination) {
      filteredUsers = filteredUsers.filter(user => 
        user.location.toLowerCase().includes(filters.destination.toLowerCase()) ||
        user.favoriteDestinations.some(dest => 
          dest.toLowerCase().includes(filters.destination.toLowerCase())
        )
      );
    }
    
    // Filter by identity markers
    if (filters.identityMarkers && filters.identityMarkers.length > 0) {
      filteredUsers = filteredUsers.filter(user => {
        // Only check users who have made these fields visible to community or public
        const canSeeGender = user.visibilitySettings.genderIdentity !== 'private';
        const canSeeOrientation = user.visibilitySettings.sexualOrientation !== 'private';
        
        return filters.identityMarkers.some(marker => {
          if (canSeeGender && user.genderIdentity) {
            if (user.genderIdentity.includes(marker)) return true;
          }
          
          if (canSeeOrientation && user.sexualOrientation) {
            if (user.sexualOrientation === marker) return true;
          }
          
          return false;
        });
      });
    }
    
    // Filter for local queers if requested
    if (filters.onlyLocalQueers) {
      filteredUsers = filteredUsers.filter(user => user.isLocalQueer);
    }
    
    return filteredUsers;
  };

  // Value object to be provided to consumers
  const value = {
    currentUser,
    isAuthenticated,
    loading,
    users,
    login,
    logout,
    updateProfile,
    toggleAnonymousMode,
    updateVisibilitySetting,
    getUserById,
    getFilteredUsers
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// Custom hook for using the user context
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
